import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import {
  Watchlist,
  WatchlistError,
} from '@terminal/stores/use-watchlist/watchlist-types';
import type { MouseEvent } from 'react';
import { useCallback } from 'react';

interface UseWatchlistManagerSaveProps {
  watchlistId?: string;
  isAddMode?: boolean;
  activeListInAddMode?: Watchlist;
  activeListInEditMode?: Watchlist;
  activeTags: string[];
  onConfirmCallback?: () => void;
  listName: string;
  setSavingInProgress: (saving: boolean) => void;
  validateForm: (params: { isAddMode: boolean; isEditMode: boolean }) => {
    isValid: boolean;
    tags: string[];
    unresolvedTags: string[];
    error?: WatchlistError;
  };
  handleValidationError: (error?: WatchlistError) => void;
  createNewList: (
    name: string,
    tags: string[],
    unresolvedTags: string[]
  ) => Promise<string | null>;
  resetName: () => void;
  updateEntitiesList: (params: {
    entitiesList: [];
    newEntities?: never;
  }) => void;
  onListCreated: (id: string) => void;
}

export const useWatchlistManagerSave = ({
  watchlistId,
  isAddMode,
  activeListInAddMode,
  activeListInEditMode,
  activeTags,
  onConfirmCallback,
  listName,
  setSavingInProgress,
  validateForm,
  handleValidationError,
  createNewList,
  resetName,
  updateEntitiesList,
  onListCreated,
}: UseWatchlistManagerSaveProps) => {
  const { updateList } = useWatchlist(state => ({
    updateList: state.updateList,
  }));
  const { trackWatchlistCreate } = usePosthogEvents();

  const onConfirmSuccess = useCallback(
    (id: string) => {
      const isCreateMode = !activeListInEditMode && !activeListInAddMode;

      if (isCreateMode) {
        resetName();
        updateEntitiesList({ entitiesList: [] });
      }

      onListCreated(id);
    },
    [
      activeListInEditMode,
      activeListInAddMode,
      resetName,
      updateEntitiesList,
      onListCreated,
    ]
  );

  const onConfirm = useCallback(async (): Promise<string | null> => {
    setSavingInProgress(true);

    try {
      const { isValid, tags, unresolvedTags, error } = validateForm({
        isAddMode: !!isAddMode,
        isEditMode: !!activeListInEditMode,
      });

      if (!isValid) {
        handleValidationError(error);
        return null;
      }

      if (activeListInEditMode) {
        await updateList({
          id: activeListInEditMode.id,
          name: activeListInEditMode.name,
          tags,
        });

        onConfirmCallback?.();

        return activeListInEditMode.id;
      }

      if (activeListInAddMode && watchlistId) {
        const mergedTags = [...tags, ...activeTags];

        await updateList({
          id: watchlistId,
          name: activeListInAddMode.name,
          tags: mergedTags,
        });

        onConfirmCallback?.();

        return watchlistId;
      }

      const listId = await createNewList(listName, tags, unresolvedTags);

      if (listId) {
        trackWatchlistCreate('watchlist');
      }

      return listId;
    } finally {
      setSavingInProgress(false);
    }
  }, [
    setSavingInProgress,
    validateForm,
    isAddMode,
    handleValidationError,
    activeListInEditMode,
    activeListInAddMode,
    watchlistId,
    updateList,
    activeTags,
    onConfirmCallback,
    createNewList,
    listName,
    trackWatchlistCreate,
  ]);

  const onSave = useCallback(
    async (e?: MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();

      const newId = await onConfirm();

      if (newId) {
        onConfirmSuccess(newId);
      }
    },
    [onConfirm, onConfirmSuccess]
  );

  return { onConfirm, onSave, onConfirmSuccess };
};
